import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import type { DocumentStatus, ProformaStatus } from '@fint/validation';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
  {
    variants: {
      variant: {
        neutral: 'bg-muted text-muted-foreground',
        info: 'bg-primary/10 text-primary',
        success: 'bg-success/10 text-success',
        warning: 'bg-warning/10 text-warning',
        danger: 'bg-destructive/10 text-destructive',
        outline: 'border border-border text-foreground',
      },
    },
    defaultVariants: { variant: 'neutral' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/**
 * How each document status looks.
 *
 * Centralised so an invoice reads identically in the list, on its own page and
 * in the customer portal. The colours carry meaning a user learns once: green
 * is settled, red needs chasing, amber is in progress, grey is not yet real.
 */
const STATUS_STYLE: Record<DocumentStatus, { label: string; variant: BadgeProps['variant'] }> = {
  draft: { label: 'Draft', variant: 'neutral' },
  sent: { label: 'Sent', variant: 'info' },
  partially_paid: { label: 'Part paid', variant: 'warning' },
  paid: { label: 'Paid', variant: 'success' },
  overdue: { label: 'Overdue', variant: 'danger' },
  void: { label: 'Void', variant: 'neutral' },
  written_off: { label: 'Written off', variant: 'neutral' },
};

export function StatusBadge({ status }: { status: DocumentStatus }) {
  const style = STATUS_STYLE[status] ?? STATUS_STYLE.draft;
  return <Badge variant={style.variant}>{style.label}</Badge>;
}

/**
 * A proforma's own vocabulary.
 *
 * Kept separate rather than folded into `STATUS_STYLE` because the two lists
 * overlap in words but not in meaning: `sent` on an invoice means money is
 * owed, and on a proforma it means an offer is out. Merging them would make a
 * proforma's badge a lie the day someone reused the invoice colour for
 * "outstanding".
 */
const PROFORMA_STATUS_STYLE: Record<ProformaStatus, { label: string; variant: BadgeProps['variant'] }> = {
  draft: { label: 'Draft', variant: 'neutral' },
  sent: { label: 'Sent', variant: 'info' },
  expired: { label: 'Expired', variant: 'warning' },
  // Green, because becoming an invoice is the outcome this document exists for.
  converted: { label: 'Converted', variant: 'success' },
  void: { label: 'Withdrawn', variant: 'neutral' },
};

export function ProformaStatusBadge({ status }: { status: ProformaStatus }) {
  const style = PROFORMA_STATUS_STYLE[status] ?? PROFORMA_STATUS_STYLE.draft;
  return <Badge variant={style.variant}>{style.label}</Badge>;
}

/**
 * Whether a business charges GST, as a chip.
 *
 * One component for the consent checklist and the Overview table, so the two
 * words are the same words. "No GST" rather than "Unregistered": the owner
 * with an LLP and a freelance book is choosing between them by this chip,
 * and "unregistered" reads as a defect rather than a fact.
 */
export function GstChip({ regime }: { regime: 'gst' | 'none' }) {
  return regime === 'gst' ? (
    <Badge variant="info">GST registered</Badge>
  ) : (
    <Badge variant="neutral">No GST</Badge>
  );
}
